import { getWriteUrl, initiateUpload, uploadFile, confirmUpload } from '../api/upload';
import type { FileMeta } from '../types';
import { useState, useCallback } from 'react';

export interface FileUploadResult {
    id: string;
    url: string;
    name: string;
}

export type UploadStatus = 'idle' | 'pending' | 'success' | 'error';

export interface UploadItem {
    localId: string;
    file: File;
    status: UploadStatus;
    progress: number;
    result?: FileUploadResult;
    error?: Error;
}

let counter = 0;
const nextId = () => `upload-${++counter}`;

export const useFileUpload = (baseURL: string, token: string) => {
    const [items, setItems] = useState<UploadItem[]>([]);

    const updateItem = useCallback((localId: string, patch: Partial<UploadItem>) => {
        setItems(prev => prev.map(item => item.localId === localId ? { ...item, ...patch } : item));
    }, []);

    const mutateMany = useCallback(async (files: File[]) => {
        if (files.length === 0) return;

        const newItems: UploadItem[] = files.map(file => ({
            localId: nextId(),
            file,
            status: 'pending' as UploadStatus,
            progress: 0,
        }));

        setItems(prev => [...prev, ...newItems]);

        const metas: FileMeta[] = files.map(file => ({
            originalName: file.name,
            mimeType: file.type,
            public: true,
        }));

        let urlMap: Record<string, { uploadURL: string; id: string; url: string; originalName: string }>;
        try {
            const { urls } = await getWriteUrl(metas, baseURL, token);
            urlMap = Object.fromEntries(urls.map(u => [u.originalName, u]));
        } catch (err: any) {
            setItems(prev =>
                prev.map(item =>
                    newItems.some(n => n.localId === item.localId)
                        ? { ...item, status: 'error', error: err }
                        : item
                )
            );
            return;
        }

        await Promise.all(
            newItems.map(async ({ localId, file }) => {
                const urlEntry = urlMap[file.name];
                if (!urlEntry) {
                    updateItem(localId, { status: 'error', error: new Error(`No signed URL for ${file.name}`) });
                    return;
                }
                const { uploadURL, id, url, originalName } = urlEntry;
                try {
                    const locationURL = await initiateUpload(uploadURL, file.type);
                    await uploadFile(locationURL, file, (percent) => {
                        updateItem(localId, { progress: percent });
                    });
                    updateItem(localId, {
                        status: 'success',
                        progress: 100,
                        result: { id, url, name: originalName },
                    });
                } catch (err: any) {
                    updateItem(localId, { status: 'error', error: err });
                }
            })
        );
    }, [baseURL, token, updateItem]);

    const mutate = useCallback((file: File) => mutateMany([file]), [mutateMany]);

    const retryItem = useCallback(async (localId: string) => {
        const item = items.find(i => i.localId === localId);
        if (!item || item.status !== 'error') return;

        updateItem(localId, { status: 'pending', progress: 0, error: undefined });

        const meta: FileMeta = { originalName: item.file.name, mimeType: item.file.type, public: true };
        try {
            const { urls } = await getWriteUrl([meta], baseURL, token);
            const { uploadURL, id, url, originalName } = urls[0];
            const locationURL = await initiateUpload(uploadURL, item.file.type);
            await uploadFile(locationURL, item.file, (percent) => {
                updateItem(localId, { progress: percent });
            });
            updateItem(localId, { status: 'success', progress: 100, result: { id, url, name: originalName } });
        } catch (err: any) {
            updateItem(localId, { status: 'error', error: err });
        }
    }, [items, baseURL, token, updateItem]);

    const removeItem = useCallback((localId: string) => {
        setItems(prev => prev.filter(i => i.localId !== localId));
    }, []);

    const clearAll = useCallback(() => {
        setItems([]);
    }, []);

    const confirmAll = useCallback(async () => {
        const ids = items
            .filter(i => i.status === 'success' && i.result?.id)
            .map(i => i.result!.id);
        await Promise.all(ids.map(id => confirmUpload(id, baseURL, token)));
    }, [items, baseURL, token]);

    const confirmItem = useCallback(async (id: string) => {
        if (!id) return;
        await confirmUpload(id, baseURL, token);
    }, [baseURL, token]);

    // Convenience single-file aliases (backward compat)
    const firstItem = items[0];
    const isPending = items.some(i => i.status === 'pending');
    const isSuccess = items.length > 0 && items.every(i => i.status === 'success');
    const error = firstItem?.error ?? null;
    const progress = firstItem?.progress ?? 0;
    const data = firstItem?.result ?? null;

    return {
        // Multi-file API
        items,
        mutateMany,
        retryItem,
        removeItem,
        clearAll,
        confirmAll,
        confirmItem,
        // Single-file backward compat
        mutate,
        isPending,
        isSuccess,
        error,
        progress,
        data,
        confirmUpload: confirmItem,
        setIsSuccess: () => {},
        setError: () => {},
    };
};